import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { PageHeader, Section, EmptyState, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { Target, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Gauge, X, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

const MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const monthLabel = (m) => { const [y, mo] = m.split("-"); return `${MONTHS[parseInt(mo,10)-1]} ${y}`; };
const shiftMonth = (m, d) => { let [y, mo] = m.split("-").map(Number); mo += d; if (mo<1){mo=12;y--;} if (mo>12){mo=1;y++;} return `${y}-${String(mo).padStart(2,"0")}`; };

export default function Analytics() {
  const { user } = useAuth();
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendeurs, setVendeurs] = useState([]);
  const [vendeurId, setVendeurId] = useState("");
  const [showObjModal, setShowObjModal] = useState(false);

  const load = () => {
    setLoading(true);
    const parts = [mode === "year" ? `year=${year}` : `month=${month}`];
    if (vendeurId) parts.push(`vendeur_id=${vendeurId}`);
    api.get(`/analytics/performance?${parts.join("&")}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, month, year, vendeurId]);
  useEffect(() => { api.get("/users").then((r) => setVendeurs(r.data.filter((u) => u.role === "vendeur"))).catch(() => {}); }, []);

  const Delta = ({ v }) => {
    if (v === null || v === undefined) return <span className="text-xs text-slate-400">—</span>;
    const up = v >= 0;
    return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(v)}%</span>;
  };
  const Kpi = ({ label, value, sub, delta, border = "border-slate-200", tid }) => (
    <div className={`bg-white p-5 rounded-sm border ${border}`} data-testid={tid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2 flex items-center justify-between">{label} {delta !== undefined && <Delta v={delta} />}</div>
      <div className="text-3xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );

  const g = data?.global;
  const isVendeur = data?.is_vendeur_view;

  return (
    <div className="p-8" data-testid="analytics-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title={isVendeur ? "Ma performance" : "Analytics & Performance"}
        subtitle={isVendeur ? "Suivez vos prospects, vos RDV et vos ventes" : "Pilotage financier — mensuel & annuel, par vendeur"}
        actions={!isVendeur && (
          <PrimaryButton onClick={() => setShowObjModal(true)} data-testid="manage-objectifs-button">
            <span className="flex items-center gap-2"><Gauge className="w-4 h-4" /> Objectifs vendeurs</span>
          </PrimaryButton>
        )}
      />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => setMode("month")} className={`px-4 py-2 text-sm font-semibold ${mode==="month"?"bg-[#111111] text-white":"text-slate-600"}`} data-testid="mode-month">Mensuel</button>
            <button onClick={() => setMode("year")} className={`px-4 py-2 text-sm font-semibold ${mode==="year"?"bg-[#111111] text-white":"text-slate-600"}`} data-testid="mode-year">Annuel</button>
          </div>
          {mode === "month" ? (
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-sm p-1">
              <button onClick={() => setMonth(shiftMonth(month,-1))} className="p-2 hover:bg-slate-50 rounded-sm" data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="px-3 text-sm font-semibold capitalize min-w-[140px] text-center" data-testid="period-label">{monthLabel(month)}</span>
              <button onClick={() => setMonth(shiftMonth(month,1))} className="p-2 hover:bg-slate-50 rounded-sm" data-testid="month-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-sm p-1">
              <button onClick={() => setYear(String(Number(year)-1))} className="p-2 hover:bg-slate-50 rounded-sm" data-testid="year-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="px-3 text-sm font-semibold min-w-[80px] text-center" data-testid="period-label">{year}</span>
              <button onClick={() => setYear(String(Number(year)+1))} className="p-2 hover:bg-slate-50 rounded-sm" data-testid="year-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
        {!isVendeur && (
          <select value={vendeurId} onChange={(e) => setVendeurId(e.target.value)} data-testid="vendeur-select" className="px-3 py-2 bg-white border border-slate-200 rounded-sm text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#B8873A]">
            <option value="">Tous les vendeurs</option>
            {vendeurs.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>

      {loading || !data ? (
        <div className="text-sm text-slate-500 py-10 text-center">Chargement…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Kpi label="Prospects reçus" value={g.recus} sub={`${g.attribues} attribués`} delta={data.comparaison.recus_delta} tid="kpi-recus" />
            <Kpi label="Ventes signées" value={g.signes} sub={g.annulations ? `${g.annulations} annulation(s)` : "0 annulation"} delta={data.comparaison.signes_delta} border="border-emerald-400" tid="kpi-signes" />
            <Kpi label="CA généré" value={formatEUR(g.ca)} sub={g.ca_annule ? `dont ${formatEUR(g.ca_annule)} annulé` : ""} delta={data.comparaison.ca_delta} border="border-[#B8873A]" tid="kpi-ca" />
            {!isVendeur ? (
              <Kpi label="Marge nette agence" value={formatEUR(g.marge_nette)} sub={`Com. agence ${formatEUR(g.commission_agence)} − vendeurs ${formatEUR(g.commission_vendeurs)}`} border="border-slate-900" tid="kpi-marge" />
            ) : (
              <Kpi label="Ma commission" value={formatEUR(g.commission_vendeurs)} sub="Sur ventes signées" border="border-slate-900" tid="kpi-ma-commission" />
            )}
          </div>

          {(g.objectif_ca > 0 || g.objectif_signatures > 0) && (
            <Section title={`Objectif ${mode === "year" ? "annuel" : "mensuel"}${isVendeur ? "" : vendeurId ? "" : " (cumul équipe)"}`}>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="objectif-gauges">
                {g.objectif_ca > 0 && (
                  <GaugeBar label="Chiffre d'affaires" current={g.ca} target={g.objectif_ca} pct={g.progress_ca} fmt={formatEUR} tid="gauge-ca" />
                )}
                {g.objectif_signatures > 0 && (
                  <GaugeBar label="Ventes signées" current={g.signes} target={g.objectif_signatures} pct={g.progress_signatures} fmt={(v) => v} tid="gauge-signatures" />
                )}
              </div>
            </Section>
          )}

          <Section title="Entonnoir de conversion">
            <div className="p-5 grid grid-cols-3 md:grid-cols-7 gap-3">
              <Funnel label="Reçus" v={g.recus} pct={100} />
              <Funnel label="R0" v={g.r0} pct={g.recus?Math.round(g.r0/g.recus*100):0} />
              <Funnel label="R1" v={g.r1} pct={g.recus?Math.round(g.r1/g.recus*100):0} />
              <Funnel label="R2" v={g.r2} pct={g.recus?Math.round(g.r2/g.recus*100):0} />
              <Funnel label="R3" v={g.r3} pct={g.recus?Math.round(g.r3/g.recus*100):0} />
              <Funnel label="Options" v={g.options} pct={g.recus?Math.round(g.options/g.recus*100):0} />
              <Funnel label="Signés" v={g.signes} pct={g.recus?Math.round(g.signes/g.recus*100):0} gold />
            </div>
          </Section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2">
              <Section title={isVendeur ? "Mon détail" : "Performance par vendeur"}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <th className="px-3 py-3">Vendeur</th>
                        <th className="px-3 py-3 text-right">Reçus</th>
                        <th className="px-3 py-3 text-right">R0/1/2/3</th>
                        <th className="px-3 py-3 text-right">Signés</th>
                        <th className="px-3 py-3 text-right">Annul.</th>
                        <th className="px-3 py-3 text-right">CA</th>
                        {!isVendeur && <th className="px-3 py-3 text-right">Marge</th>}
                        <th className="px-3 py-3 text-right min-w-[140px]">Objectif CA</th>
                        <th className="px-3 py-3 text-right">Taux</th>
                      </tr>
                    </thead>
                    <tbody data-testid="vendeur-table">
                      {data.par_vendeur.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-500">Aucune donnée sur cette période.</td></tr>
                      ) : data.par_vendeur.map((v, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vendeur-row-${i}`}>
                          <td className="px-3 py-3 font-medium">{v.name}</td>
                          <td className="px-3 py-3 text-right">{v.recus}</td>
                          <td className="px-3 py-3 text-right text-slate-600">{v.r0}/{v.r1}/{v.r2}/{v.r3}</td>
                          <td className="px-3 py-3 text-right font-semibold text-emerald-700">{v.signes}</td>
                          <td className="px-3 py-3 text-right text-red-600">{v.annulations}</td>
                          <td className="px-3 py-3 text-right font-semibold">{formatEUR(v.ca)}</td>
                          {!isVendeur && <td className="px-3 py-3 text-right">{formatEUR(v.marge_nette)}</td>}
                          <td className="px-3 py-3 text-right">
                            {v.objectif_ca > 0 ? (
                              <div className="flex flex-col items-end gap-1" data-testid={`vendeur-objectif-${i}`}>
                                <div className="text-xs text-slate-500">{v.progress_ca}% · {formatEUR(v.objectif_ca)}</div>
                                <div className="h-1.5 w-28 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full ${v.progress_ca >= 100 ? "bg-emerald-500" : "bg-[#B8873A]"}`} style={{ width: `${Math.min(v.progress_ca, 100)}%` }} />
                                </div>
                              </div>
                            ) : <span className="text-xs text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-600">{v.taux_signature}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </div>
            <div>
              <Section title="Sources des leads">
                <div className="p-4 space-y-2">
                  {data.sources.length === 0 ? <EmptyState icon={Target} title="Aucune source" description="" /> : data.sources.map((s, i) => {
                    const max = data.sources[0].count || 1;
                    return (
                      <div key={i} data-testid={`source-${i}`}>
                        <div className="flex justify-between text-xs mb-1"><span className="font-medium text-[#111111]">{s.source}</span><span className="text-slate-500">{s.count}</span></div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#B8873A]" style={{ width: `${Math.round(s.count/max*100)}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            </div>
          </div>
        </>
      )}
      {showObjModal && <ObjModal vendeurs={vendeurs} onClose={() => setShowObjModal(false)} onSaved={() => { setShowObjModal(false); load(); }} />}
    </div>
  );
}

function GaugeBar({ label, current, target, pct, fmt, tid }) {
  const reached = pct >= 100;
  return (
    <div data-testid={tid}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <span className={`text-sm font-bold ${reached ? "text-emerald-600" : "text-[#111111]"}`}>{pct}%</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-[width] duration-500 ${reached ? "bg-emerald-500" : "bg-[#B8873A]"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-xs text-slate-500">
        <span className="font-semibold text-[#111111]">{fmt(current)}</span>
        <span>Objectif {fmt(target)}</span>
      </div>
    </div>
  );
}

function ObjModal({ vendeurs, onClose, onSaved }) {
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/objectifs").then((r) => {
      const m = {};
      vendeurs.forEach((v) => {
        const o = r.data[v.id] || {};
        m[v.id] = { objectif_ca: o.objectif_ca || 0, objectif_signatures: o.objectif_signatures || 0 };
      });
      setRows(m);
    });
  }, [vendeurs]);

  const setVal = (vid, key, val) => setRows((p) => ({ ...p, [vid]: { ...p[vid], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(vendeurs.map((v) =>
        api.put(`/objectifs/${v.id}`, {
          objectif_ca: Number(rows[v.id]?.objectif_ca) || 0,
          objectif_signatures: Number(rows[v.id]?.objectif_signatures) || 0,
        })
      ));
      toast.success("Objectifs enregistrés");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="objectifs-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-bold text-[#111111]" style={{ fontFamily: "Cabinet Grotesk" }}>Objectifs mensuels par vendeur</h2>
            <p className="text-xs text-slate-500 mt-0.5">CA et signatures à atteindre chaque mois. La vue annuelle multiplie par 12.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="close-objectifs-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {vendeurs.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Aucun vendeur enregistré.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2">Vendeur</th>
                  <th className="py-2 text-right">Objectif CA (€/mois)</th>
                  <th className="py-2 text-right">Signatures / mois</th>
                </tr>
              </thead>
              <tbody>
                {vendeurs.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-2.5 font-medium">{v.name}</td>
                    <td className="py-2.5 text-right">
                      <input type="number" min="0" step="1000" value={rows[v.id]?.objectif_ca ?? 0}
                        onChange={(e) => setVal(v.id, "objectif_ca", e.target.value)}
                        data-testid={`obj-ca-${v.id}`}
                        className="w-36 px-2 py-1.5 border border-slate-300 rounded-sm text-sm text-right focus:outline-none focus:ring-1 focus:ring-[#B8873A]" />
                    </td>
                    <td className="py-2.5 text-right">
                      <input type="number" min="0" step="1" value={rows[v.id]?.objectif_signatures ?? 0}
                        onChange={(e) => setVal(v.id, "objectif_signatures", e.target.value)}
                        data-testid={`obj-sig-${v.id}`}
                        className="w-20 px-2 py-1.5 border border-slate-300 rounded-sm text-sm text-right focus:outline-none focus:ring-1 focus:ring-[#B8873A]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex justify-end gap-2 pt-5">
            <SecondaryButton type="button" onClick={onClose}>Annuler</SecondaryButton>
            <PrimaryButton onClick={save} disabled={saving} data-testid="save-objectifs-btn">
              <span className="flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? "…" : "Enregistrer"}</span>
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function Funnel({ label, v, pct, gold }) {
  return (
    <div className={`border rounded-sm p-3 ${gold ? "border-[#B8873A] bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{v}</div>
      <div className="text-xs text-slate-500">{pct}%</div>
    </div>
  );
}
